import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { MultiSelect } from 'primereact/multiselect';
import { SelectButton } from 'primereact/selectbutton';
import { TabPanel, TabView } from 'primereact/tabview';
import { classNames } from 'primereact/utils';
import { Combobox, formatDateTime, InputDate, RColumn, RemoteTable, GetItemsProps } from 'medere-ui-framework';
import { ComboDataDTO } from '../../dto/ComboDataDTO';
import { DrugTagDTO } from '../../dto/DrugTagDTO';
import {
    DrugItemComboDTO,
    DrugTagCostSort,
    DrugTagCostValuation,
    DrugTagPrescriptionCostCountDTO,
    DrugTagPrescriptionReportFilter,
    DrugTagPrescriptionReportIndicatorsDTO,
    DrugTagPrescriptionReportItemDTO,
    DrugTagReportApplicationState,
    DrugTagReportExportFormat,
} from '../../dto/DrugTagPrescriptionReportDTO';
import { getDrugTags } from '../../services/DrugTagController';
import {
    getDrugTagPrescriptionCostCount,
    getDrugTagPrescriptionReport,
    getDrugTagPrescriptionReportIndicators,
    getDrugItemsForCombo,
    startDrugTagPrescriptionCostExport,
    startDrugTagPrescriptionReportExport,
} from '../../services/DrugTagPrescriptionReportController';
import { getProfessionals } from '../../servicesHooks/Budget/useBudgetService';
import { useEntityService } from '../../servicesHooks/Entity/useEntityService';
import { parseApiError } from '../../services/apiError';
import { toISOString } from '../../utils/localeHelpers';
import useNotify from '../../hooks/useNotify';
import useAsyncExport from '../../hooks/useAsyncExport';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import NvTooltips from '../../components/novarium/NvTooltips';
import DrugTagCostTable from './DrugTagCostTable';
import DrugTagPrescriptionDetailDrawer from './DrugTagPrescriptionDetailDrawer';
import DrugTagReportIndicators from './DrugTagReportIndicators';
import TwoLines from './TwoLines';
import '../../components/novarium/ExportMenu.scss';
import './DrugTagPrescriptionReportView.scss';

/**
 * Las mismas categorías que el resumen, para listar y exportar las que se elijan. Lo preparado sin aplicar es
 * No aplicadas + Suspendidas con "Solo con dosis preparadas".
 */
const APPLICATION_STATES: { value: DrugTagReportApplicationState; label: string }[] = [
    { value: 'APPLIED', label: 'Aplicadas' },
    { value: 'NOT_APPLIED', label: 'No aplicadas' },
    { value: 'SUSPENDED', label: 'Suspendidas' },
    { value: 'PENDING', label: 'Pendientes' },
];

const CONSULT_ERROR = 'No se pudo consultar el reporte. Intentá de nuevo.';
const EXPORT_ERROR = 'No se pudo exportar el reporte. Intentá de nuevo.';
const NOT_CONSULTED = 'Elegí el rango y consultá el reporte.';
const integer = new Intl.NumberFormat('es-AR');

const DOSES_TAB = 0;
const COST_TAB = 1;

const VALUATIONS: { value: DrugTagCostValuation; label: string }[] = [
    { value: 'COST', label: 'Costo' },
    { value: 'PRICE', label: 'Precio de venta' },
];

/** Aviso de las dosis con alguna presentación sin el valor elegido: cuentan, pero no suman. */
function withoutValueNotice(doses: number, valuation: DrugTagCostValuation) {
    const value = valuation === 'PRICE' ? 'sin precio de venta' : 'sin costo cargado';
    const amounts = valuation === 'PRICE' ? 'los precios' : 'los costos';
    return doses === 1
        ? `dosis tiene alguna presentación ${value}: cuenta, pero no suma en ${amounts}.`
        : `dosis tienen alguna presentación ${value}: cuentan, pero no suman en ${amounts}.`;
}

interface ExportButtonProps {
    /** Por qué no se puede exportar todavía; undefined si se puede. */
    disabledReason?: string;
    exporting: boolean;
    progress: number | null;
    /** Formato que se está generando: el PDF no informa avance. */
    format?: DrugTagReportExportFormat;
    /** Con PDF, el botón abre un menú «Descargar PDF» / «Descargar Excel»; sin él, exporta directo el Excel. */
    withPdf?: boolean;
    onExport: (format: DrugTagReportExportFormat) => void;
}

/** Exportar, con el avance mientras se genera en segundo plano y el motivo si está deshabilitado. */
function ExportButton({ disabledReason, exporting, progress, format = 'xlsx', withPdf = false, onExport }: ExportButtonProps) {
    const menuRef = useRef<Menu>(null);
    const items: MenuItem[] = [
        { label: 'Descargar PDF', icon: 'pi pi-file-pdf', command: () => onExport('pdf') },
        { label: 'Descargar Excel', icon: 'pi pi-file-excel', command: () => onExport('xlsx') },
    ];
    return (
        <div className="nv-row-8">
            {exporting && (
                <span className="nv-muted" role="status">
                    {format === 'pdf' ? 'Generando PDF…' : `Generando Excel… ${progress ?? 0} %`}
                </span>
            )}
            {/* El motivo de deshabilitado va en un span propio (nv-disabled-tip), no con showOnDisabled:
                ese tooltip de PrimeReact mueve el botón dentro de otro span por fuera de React. */}
            <span
                className={classNames({ 'nv-tip nv-disabled-tip': disabledReason })}
                data-pr-tooltip={disabledReason}
                tabIndex={disabledReason ? 0 : undefined}
            >
                {/* TODO(design-system): secundario con menú — Novarium define «Exportar» como secundario pero no uno que abra un menú. */}
                {withPdf && <Menu model={items} popup ref={menuRef} className="nv-scope nv-export-menu" />}
                <Button
                    className="buttonMain2"
                    outlined
                    icon={withPdf ? 'pi pi-download' : 'pi pi-file-excel'}
                    label="Exportar"
                    loading={exporting}
                    disabled={!!disabledReason}
                    aria-haspopup={withPdf ? 'menu' : undefined}
                    onClick={event => (withPdf ? menuRef.current?.toggle(event) : onExport('xlsx'))}
                />
            </span>
        </div>
    );
}

type FieldName = 'initialDate' | 'finalDate';
type FieldErrors = Partial<Record<FieldName, string>>;

/** Lo que tiene el Combobox mientras se tipea (sin id) o cuando se eligió una opción. */
interface PatientOption { medereentity?: number; fullname?: string }
type DrugItemOption = Partial<DrugItemComboDTO>;

/**
 * Si el medicamento elegido sigue valiendo con esas etiquetas: sin etiquetas vale cualquiera; con etiquetas, solo uno
 * que tenga alguna. Lo tipeado sin elegir no se toca.
 */
const keepsDrugItem = (drugItem: DrugItemOption | null, tagIds: number[]) =>
    !drugItem?.drugTagIds || tagIds.length === 0 || drugItem.drugTagIds.some(id => tagIds.includes(id));

const text = (value: string | number | null) => (value === null || value === '' ? '—' : value);
const dateTime = (value: number | null) => (value ? formatDateTime(value) : '—');

// Cuerpos de las columnas a nivel de módulo: RemoteTable necesita funciones estables.
type Row = DrugTagPrescriptionReportItemDTO;
const scheduledDateBody = (row: Row) => dateTime(row.scheduledDate);
const patientBody = (row: Row) => text(row.patientName);
/** El medicamento y, debajo, la presentación que se preparó; si no se preparó o se descartó, lo dice. */
const drugsBody = (row: Row) => (
    <TwoLines main={String(text(row.drugs))} detail={row.preparedPresentationNames ?? preparationMissing(row)} />
);
function preparationMissing(row: Row) {
    if (row.preparationFate === 'DISCARDED') return 'Preparación descartada';
    return row.applicationDate ? 'Sin preparación registrada' : 'Sin preparar';
}
const drugTagsBody = (row: Row) => text(row.drugTags);
const healthEntityBody = (row: Row) => text(row.healthEntityName);
const stateBody = (row: Row) => text(row.applicationStateDescription);
const SCHEDULED_DATE_STYLE = { width: 150 };
const STATE_STYLE = { width: 130 };
/** El buscador de medicamento de cada grilla consulta al dejar de escribir. */
const SEARCH_DELAY_MS = 400;

function firstDayOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * Hasta dónde llega el rango por defecto: ayer. Las dosis de hoy cuyo horario ya pasó todavía se están registrando
 * (la mitad se registra más de una hora después) y contarían como no aplicadas; quien elige hoy lo hace sabiendo eso.
 */
function yesterday(): Date {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
}

/** dd/mm/aaaa, como lo escribe el backend en el resto del encabezado del PDF. */
function shortDate(date: Date): string {
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function isoDate(date: Date): string {
    return toISOString(date)!.substring(0, 10);
}

/**
 * Medicación prescripta por etiqueta (ej. "Alto costo") a pacientes internados, en dos tabs con los mismos filtros:
 * Dosis (resumen de prescripto contra aplicado y una fila por dosis, con si se aplicó y cuándo) y Costo (por obra
 * social y medicamento, lo aplicado y lo preparado sin aplicar, con su costo). El rango de fechas es sobre la fecha
 * programada de cada dosis. Las etiquetas se asignan en Artículos › Medicamentos.
 */
export default function DrugTagPrescriptionReportView() {
    const notify = useNotify();
    const { getPatients, getHealthEntityComboDataDTO } = useEntityService();
    /** Pacientes para el Combobox (getPatients no tiene la firma de GetItemsProps). */
    const getPatientsForCombo = ({ filterValue, offSet, pageSize }: GetItemsProps) =>
        getPatients(filterValue ?? '', offSet ?? 0, pageSize ?? 20);
    // Del primer día del mes de ayer (el día 1, el mes anterior entero) hasta ayer.
    const [defaultToDate] = useState(yesterday);

    const [drugTags, setDrugTags] = useState<DrugTagDTO[]>([]);
    const [fromDate, setFromDate] = useState<Date | null>(() => firstDayOfMonth(defaultToDate));
    const [toDate, setToDate] = useState<Date | null>(defaultToDate);
    const [drugTagIds, setDrugTagIds] = useState<number[]>([]);
    const [drugItem, setDrugItem] = useState<DrugItemOption | null>(null);
    /** El combo de medicación se recarga solo cuando cambian sus params. */
    const drugItemParams = useMemo(() => ({ drugTagIds }), [drugTagIds]);
    const [professional, setProfessional] = useState<ComboDataDTO | null>(null);
    const [patient, setPatient] = useState<PatientOption | null>(null);
    const [healthEntity, setHealthEntity] = useState<ComboDataDTO | null>(null);
    const [applicationStates, setApplicationStates] = useState<DrugTagReportApplicationState[]>([]);
    const [onlyPrepared, setOnlyPrepared] = useState(false);
    /** Buscador de medicamento de la grilla de dosis. */
    const [doseSearch, setDoseSearch] = useState('');
    const doseDrugSearch = useDebouncedValue(doseSearch.trim(), SEARCH_DELAY_MS);
    const [errors, setErrors] = useState<FieldErrors>({});

    /**
     * Filtros de la última consulta (sin los de la grilla de dosis): cada "Consultar" arma uno nuevo y recargan las
     * cards, la grilla y la tab Costo.
     */
    const [appliedFilter, setAppliedFilter] = useState<DrugTagPrescriptionReportFilter | null>(null);
    /**
     * La consulta más Estado, "Solo con dosis preparadas" y el buscador de medicamento: cambiarlos recarga solo la
     * grilla de dosis y su contador.
     */
    const doseFilter = useMemo<DrugTagPrescriptionReportFilter | null>(() => appliedFilter && {
        ...appliedFilter,
        applicationStates: applicationStates.length > 0 ? applicationStates.join(',') : undefined,
        onlyPrepared: onlyPrepared || undefined,
        drugSearch: doseDrugSearch || undefined,
    }, [appliedFilter, applicationStates, onlyPrepared, doseDrugSearch]);
    const latestDoseFilterRef = useRef<DrugTagPrescriptionReportFilter | null>(null);
    /** Cards de la última consulta: no dependen de los filtros de la grilla. */
    const [indicators, setIndicators] = useState<DrugTagPrescriptionReportIndicatorsDTO | null>(null);
    /** Filas de la grilla de dosis con sus filtros. */
    const [doseRows, setDoseRows] = useState<number | null>(null);
    const [consulting, setConsulting] = useState(false);
    const { run: runExport, exporting, progress: exportProgress } = useAsyncExport();
    const [exportFormat, setExportFormat] = useState<DrugTagReportExportFormat>('xlsx');
    /** Lo elegido en la última consulta, con los nombres, para el encabezado del PDF (el borrador puede haber cambiado). */
    const [appliedSummary, setAppliedSummary] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState(DOSES_TAB);
    /** Contador de la tab Costo, de la consulta para la que se pidió (se pide al abrir la tab). */
    const [costCount, setCostCount] = useState<DrugTagPrescriptionCostCountDTO | null>(null);
    const costCountFilterRef = useRef<DrugTagPrescriptionReportFilter | null>(null);
    /** Con qué se valoriza la tab Costo: cambiarlo recarga la tab sin volver a consultar. */
    const [valuation, setValuation] = useState<DrugTagCostValuation>('COST');
    /** Buscador de medicamento de la grilla de costo. */
    const [costSearch, setCostSearch] = useState('');
    const costDrugSearch = useDebouncedValue(costSearch.trim(), SEARCH_DELAY_MS);
    /**
     * La consulta más la valorización y el buscador: alimenta la grilla, el contador, el detalle y el export de la tab
     * Costo. El backend guarda el cálculo de la consulta, así que cambiarlos no lo repite.
     */
    const costFilter = useMemo<DrugTagPrescriptionReportFilter | null>(
        () => appliedFilter && { ...appliedFilter, valuation, drugSearch: costDrugSearch || undefined },
        [appliedFilter, valuation, costDrugSearch]);
    /** Orden actual de la grilla de costo, para exportar en el mismo orden. */
    const costSortRef = useRef<DrugTagCostSort>({});
    const { run: runCostExport, exporting: exportingCost, progress: costExportProgress } = useAsyncExport();
    /** Dosis cuyo detalle está abierto en el panel lateral. */
    const [viewing, setViewing] = useState<DrugTagPrescriptionReportItemDTO | null>(null);

    const rowActions = useCallback((row: DrugTagPrescriptionReportItemDTO) => (
        <Button
            type="button"
            text
            className="nv-icon nv-tip"
            icon="pi pi-eye"
            aria-label={`Ver detalle de ${row.patientName ?? 'la dosis'}`}
            data-pr-tooltip="Ver detalle"
            onClick={() => setViewing(row)}
        />
    ), []);

    useEffect(() => {
        getDrugTags()
            .then(setDrugTags)
            .catch(() => notify.error('No se pudieron cargar las etiquetas de medicamentos. Intentá de nuevo.'));
    }, [notify]);

    function validate(): FieldErrors {
        const result: FieldErrors = {};
        if (!fromDate) result.initialDate = 'Ingresá la fecha desde';
        if (!toDate) result.finalDate = 'Ingresá la fecha hasta';
        else if (fromDate && fromDate > toDate) result.finalDate = 'Ingresá una fecha igual o posterior a la fecha desde';
        return result;
    }

    /** El rango y los filtros elegidos, con sus nombres, uno por dato: el encabezado del PDF. */
    function describeConsult(): string[] {
        const parts = [`Programada: ${shortDate(fromDate!)} al ${shortDate(toDate!)}`];
        const tagNames = drugTags.filter(tag => drugTagIds.includes(tag.drugTag)).map(tag => tag.name);
        if (tagNames.length > 0) parts.push(`Etiquetas: ${tagNames.join(', ')}`);
        if (drugItem?.name) parts.push(`Medicamento: ${drugItem.name}`);
        if (professional?.name) parts.push(`Profesional: ${professional.name}`);
        if (patient?.fullname) parts.push(`Paciente: ${patient.fullname}`);
        if (healthEntity?.name) parts.push(`Obra social: ${healthEntity.name}`);
        return parts;
    }

    function handleConsult() {
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        const filter: DrugTagPrescriptionReportFilter = {
            initialDate: isoDate(fromDate!),
            finalDate: isoDate(toDate!),
            drugTagIds: drugTagIds.length > 0 ? drugTagIds.join(',') : undefined,
            drugItemId: drugItem?.id ?? undefined,
            professionalId: professional?.id ?? undefined,
            patientId: patient?.medereentity ?? undefined,
            healthEntityId: healthEntity?.id ?? undefined,
        };
        setAppliedFilter(filter);
        setAppliedSummary(describeConsult());
        setIndicators(null);
        setCostCount(null);
        costCountFilterRef.current = null;
        setConsulting(true);
    }

    /**
     * Cards y contador de la grilla: con cada consulta y cada cambio de los filtros de la grilla. Las cards no cambian
     * con esos filtros (el backend las cuenta sin ellos); el contador sí.
     */
    useEffect(() => {
        if (!doseFilter) return;
        latestDoseFilterRef.current = doseFilter;
        setDoseRows(null);
        getDrugTagPrescriptionReportIndicators(doseFilter)
            .then(result => {
                if (latestDoseFilterRef.current !== doseFilter) return;
                setIndicators(result);
                setDoseRows(result.rows);
            })
            .catch((error: unknown) => {
                if (latestDoseFilterRef.current === doseFilter) showError(error, CONSULT_ERROR);
            })
            .finally(() => { if (latestDoseFilterRef.current === doseFilter) setConsulting(false); });
        // showError lee el estado del momento; la consulta depende solo del filtro.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doseFilter]);

    /** Contador de la tab Costo; el backend guarda el cálculo, así que la primera página no lo repite. */
    function loadCostCount(filter: DrugTagPrescriptionReportFilter) {
        costCountFilterRef.current = filter;
        getDrugTagPrescriptionCostCount(filter)
            .then(result => { if (costCountFilterRef.current === filter) setCostCount(result); })
            .catch((error: unknown) => { if (costCountFilterRef.current === filter) showError(error, CONSULT_ERROR); });
    }

    function handleTabChange(index: number) {
        setActiveTab(index);
    }

    /** El contador de la tab Costo se pide al abrirla, con cada consulta y con cada cambio de valorización o búsqueda. */
    useEffect(() => {
        if (activeTab === COST_TAB && costFilter && costCountFilterRef.current !== costFilter) {
            setCostCount(null);
            loadCostCount(costFilter);
        }
        // loadCostCount solo usa refs y setters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, costFilter]);

    /** Página para la tabla. Si falla, avisa con el mensaje del backend y la tabla queda como está. */
    function loadPage(offset: number, pageSize: number): Promise<DrugTagPrescriptionReportItemDTO[]> {
        if (!doseFilter) return Promise.resolve([]);
        return getDrugTagPrescriptionReport(doseFilter, offset, pageSize).catch((error: unknown) => {
            showError(error, CONSULT_ERROR);
            return [];
        });
    }

    /** Errores de un dato van al campo; el resto, a la card flotante. */
    function showError(error: unknown, fallback: string) {
        const apiError = parseApiError(error);
        const fieldErrors: FieldErrors = {};
        for (const detail of apiError.details ?? []) {
            if (detail.field === 'initialDate' || detail.field === 'finalDate') {
                fieldErrors[detail.field] = detail.message;
            }
        }
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return;
        }
        const isBusinessError = apiError.status >= 400 && apiError.status < 500;
        notify.error(isBusinessError ? apiError.message : fallback);
    }

    /** Por qué no se puede exportar todavía; undefined si se puede. */
    const exportDisabledReason = !doseFilter
        ? 'Consultá el reporte para poder exportarlo'
        : doseRows === 0 ? 'No hay dosis para exportar' : undefined;

    const costExportDisabledReason = !costFilter
        ? 'Consultá el reporte para poder exportarlo'
        : costCount?.rows === 0 ? 'No hay filas para exportar' : undefined;

    /**
     * El archivo se genera en segundo plano y se descarga solo al terminar. El Excel trae todas las dosis (las columnas
     * las define el backend); el PDF, las primeras 2.000, con el resumen de filtros de la última consulta y de la grilla.
     */
    function handleExport(format: DrugTagReportExportFormat) {
        if (!doseFilter) return;
        const filter = doseFilter;
        const stateLabels = APPLICATION_STATES.filter(state => applicationStates.includes(state.value)).map(state => state.label);
        const filtersText = [
            ...appliedSummary,
            ...(stateLabels.length > 0 ? [`Estado: ${stateLabels.join(', ')}`] : []),
            ...(onlyPrepared ? ['Solo con dosis preparadas'] : []),
            ...(filter.drugSearch ? [`Medicamento que contiene: ${filter.drugSearch}`] : []),
        ].join(' · ');
        setExportFormat(format);
        runExport(() => startDrugTagPrescriptionReportExport(filter, format, filtersText), EXPORT_ERROR);
    }

    /** El de la tab Costo sale en el orden en que está la grilla. */
    function handleCostExport() {
        if (!costFilter) return;
        const filter = costFilter;
        const sort = costSortRef.current;
        runCostExport(() => startDrugTagPrescriptionCostExport(filter, sort), EXPORT_ERROR);
    }

    return (
        <div className="nv-scope nv-page drug-tag-report">
            <NvTooltips />
            <header className="nv-page-head">
                <h1 className="nv-h1">Medicación prescripta por etiqueta</h1>
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    Lo prescripto a pacientes internados de la medicación o las etiquetas elegidas; sin ninguna, de
                    toda la medicación. Las fechas filtran por el horario programado de cada dosis. Dosis muestra si cada una se aplicó; Costo, cuánto
                    costó lo aplicado y lo que se preparó sin aplicar, o cuánto se dejaría de facturar a precio de venta.
                </p>
            </header>

            <section className="nv-section" aria-label="Filtros">
                <div className="nv-form-row">
                    <InputDate
                        label="Programada desde"
                        required
                        className="nv-w-s"
                        value={fromDate}
                        onChange={(e: { value?: Date | null }) => {
                            setFromDate(e.value ?? null);
                            setErrors(current => ({ ...current, initialDate: undefined, finalDate: undefined }));
                        }}
                        error={!!errors.initialDate}
                        caption={errors.initialDate}
                    />
                    <InputDate
                        label="Programada hasta"
                        required
                        className="nv-w-s"
                        value={toDate}
                        onChange={(e: { value?: Date | null }) => {
                            setToDate(e.value ?? null);
                            setErrors(current => ({ ...current, finalDate: undefined }));
                        }}
                        error={!!errors.finalDate}
                        caption={errors.finalDate}
                    />
                    <div className="nv-field nv-w-l">
                        <label htmlFor="drug-tag-report-tags">Etiquetas</label>
                        <MultiSelect
                            inputId="drug-tag-report-tags"
                            value={drugTagIds}
                            options={drugTags}
                            optionLabel="name"
                            optionValue="drugTag"
                            display="chip"
                            filter
                            placeholder={drugTags.length > 0
                                ? 'Todas'
                                : 'No hay etiquetas: se cargan en Medicamentos - Etiquetas'}
                            emptyFilterMessage="No hay etiquetas con ese nombre"
                            onChange={(e) => {
                                const tagIds: number[] = e.value ?? [];
                                setDrugTagIds(tagIds);
                                if (!keepsDrugItem(drugItem, tagIds)) setDrugItem(null);
                            }}
                        />
                    </div>
                    <Combobox
                        label="Medicación"
                        className="nv-w-m"
                        getItems={getDrugItemsForCombo}
                        params={drugItemParams}
                        optionLabel="name"
                        placeholder="Todos"
                        forceSelection={false}
                        autoselectOnOneItem={false}
                        value={drugItem}
                        setValue={(value: DrugItemOption | null) => setDrugItem(value)}
                    />
                </div>
                <div className="nv-form-row">
                    <Combobox
                        label="Profesional"
                        className="nv-w-m"
                        getItems={getProfessionals}
                        optionLabel="name"
                        placeholder="Todos"
                        forceSelection={false}
                        value={professional}
                        setValue={(value: ComboDataDTO | null) => setProfessional(value)}
                    />
                    <Combobox
                        label="Paciente"
                        className="nv-w-m"
                        getItems={getPatientsForCombo}
                        optionLabel="fullname"
                        placeholder="Todos"
                        forceSelection={false}
                        value={patient}
                        setValue={(value: PatientOption | null) => setPatient(value)}
                    />
                    <Combobox
                        label="Obra social"
                        className="nv-w-m"
                        getItems={getHealthEntityComboDataDTO}
                        optionLabel="name"
                        placeholder="Todas"
                        forceSelection={false}
                        value={healthEntity}
                        setValue={(value: ComboDataDTO | null) => setHealthEntity(value)}
                    />
                </div>
                <div className="nv-row-8">
                    <Button className="buttonMain3" label="Consultar" loading={consulting} onClick={handleConsult} />
                </div>
            </section>

            <TabView activeIndex={activeTab} onTabChange={(e) => handleTabChange(e.index)}>
                <TabPanel header="Dosis">
                    <div className="drug-tag-report__tab">
                        {indicators && <DrugTagReportIndicators indicators={indicators} />}

                        <section className="nv-section" aria-label="Dosis">
                            {/* Filtros de la grilla: cambian la tabla, su contador y el Excel; las cards no. */}
                            <div className="drug-tag-report__grid-head">
                                <div className="drug-tag-report__grid-filters">
                                    <div className="nv-field nv-w-m">
                                        <label htmlFor="drug-tag-report-search">Buscar medicamento</label>
                                        <span className="p-input-icon-left">
                                            <i className="pi pi-search" aria-hidden="true" />
                                            <InputText
                                                id="drug-tag-report-search"
                                                type="search"
                                                value={doseSearch}
                                                onChange={(e) => setDoseSearch(e.target.value)}
                                            />
                                        </span>
                                    </div>
                                    <div className="nv-field nv-w-m">
                                        <label htmlFor="drug-tag-report-state">Estado</label>
                                        <MultiSelect
                                            inputId="drug-tag-report-state"
                                            value={applicationStates}
                                            options={APPLICATION_STATES}
                                            optionLabel="label"
                                            optionValue="value"
                                            display="chip"
                                            placeholder="Todas"
                                            onChange={(e) => setApplicationStates(e.value ?? [])}
                                        />
                                    </div>
                                    <div className="nv-check-row drug-tag-report__check">
                                        <Checkbox
                                            inputId="drug-tag-report-prepared"
                                            checked={onlyPrepared}
                                            onChange={(e) => setOnlyPrepared(!!e.checked)}
                                        />
                                        <label htmlFor="drug-tag-report-prepared">Solo con dosis preparadas</label>
                                    </div>
                                </div>
                                <div className="nv-row-8 drug-tag-report__grid-actions">
                                    <span className="nv-muted">
                                        {doseRows === null ? '' : `${integer.format(doseRows)} dosis`}
                                    </span>
                                    <ExportButton
                                        disabledReason={exportDisabledReason}
                                        exporting={exporting}
                                        progress={exportProgress}
                                        format={exportFormat}
                                        withPdf
                                        onExport={handleExport}
                                    />
                                </div>
                            </div>

                            {doseFilter ? (
                                <RemoteTable
                                    idTable="drugTagPrescriptionReport"
                                    dataKey="prescriptionApplication"
                                    getItems={loadPage}
                                    params={doseFilter}
                                    paramsDelay={0}
                                    filterRow={false}
                                    rowActions={rowActions}
                                    notFlex
                                    maxHeight={560}
                                    tableMinWidth={900}
                                    notFoundText="No hay dosis para estos filtros."
                                >
                                    <RColumn field="scheduledDate" header="Fecha programada"
                                        className="drug-tag-report__center" style={SCHEDULED_DATE_STYLE}
                                        body={scheduledDateBody} />
                                    <RColumn field="patientName" header="Paciente" grow={2} body={patientBody} />
                                    <RColumn field="drugs" header="Medicamento" grow={2} body={drugsBody} />
                                    <RColumn field="drugTags" header="Etiqueta" body={drugTagsBody} />
                                    <RColumn field="healthEntityName" header="Obra social" body={healthEntityBody} />
                                    <RColumn field="applicationStateDescription" header="Estado" style={STATE_STYLE}
                                        body={stateBody} />
                                </RemoteTable>
                            ) : (
                                <p className="nv-muted">{NOT_CONSULTED}</p>
                            )}
                            {viewing && (
                                <DrugTagPrescriptionDetailDrawer
                                    key={viewing.prescriptionApplication}
                                    item={viewing}
                                    onHide={() => setViewing(null)}
                                />
                            )}
                        </section>
                    </div>
                </TabPanel>

                <TabPanel header="Costo">
                    <section className="nv-section drug-tag-report__tab" aria-label="Costo">
                        <div className="nv-section-head">
                            <div className="drug-tag-report__grid-filters">
                                {/* El mismo contenido en otra medida: un SelectButton, no pestañas. */}
                                <SelectButton
                                    value={valuation}
                                    options={VALUATIONS}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(event) => { if (event.value) setValuation(event.value as DrugTagCostValuation); }}
                                    aria-label="Valorizar a"
                                />
                                <span className="p-input-icon-left nv-w-m">
                                    <i className="pi pi-search" aria-hidden="true" />
                                    <InputText
                                        type="search"
                                        value={costSearch}
                                        onChange={(e) => setCostSearch(e.target.value)}
                                        placeholder="Buscar medicamento"
                                        aria-label="Buscar medicamento"
                                    />
                                </span>
                            </div>
                            <div className="nv-row-8">
                                <span className="nv-muted">
                                    {costCount === null ? '' : `${integer.format(costCount.rows)} ${costCount.rows === 1 ? 'fila' : 'filas'}`}
                                </span>
                                <ExportButton
                                    disabledReason={costExportDisabledReason}
                                    exporting={exportingCost}
                                    progress={costExportProgress}
                                    onExport={handleCostExport}
                                />
                            </div>
                        </div>
                        {costCount !== null && costCount.dosesWithoutCost > 0 && (
                            <p className="nv-help">
                                <i className="pi pi-info-circle" aria-hidden="true" />{' '}
                                {integer.format(costCount.dosesWithoutCost)}{' '}
                                {withoutValueNotice(costCount.dosesWithoutCost, valuation)}
                            </p>
                        )}
                        {costFilter ? (
                            <DrugTagCostTable
                                filter={costFilter}
                                onSortChange={(sort) => { costSortRef.current = sort; }}
                                onError={(error) => showError(error, CONSULT_ERROR)}
                            />
                        ) : (
                            <p className="nv-muted">{NOT_CONSULTED}</p>
                        )}
                    </section>
                </TabPanel>
            </TabView>
        </div>
    );
}
